/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import { getUserLanguageCode } from 'js/lib/language';

import { Button, Typography2 } from '@coursera/cds-core';
import { TranslationIcon } from '@coursera/cds-icons';

import LanguageTranslationFeedbackSubMenu from 'bundles/composable-page-header/components/mobile-menu-items/LanguageTranslationFeedbackSubMenu';
import MobileBackNavHeader from 'bundles/composable-page-header/components/mobile-menu-items/MobileBackNavHeader';
import type { MobileLanguageIdentifierValues } from 'bundles/composable-page-header/constants';
import { MOBILE_LANGUAGE_IDENTIFIER, MOBILE_MAIN_IDENTIFIER } from 'bundles/composable-page-header/constants';
import { useMobileToParentPage } from 'bundles/composable-page-header/contexts/MobileNavigationContext';
import TranslationDropdownMenu from 'bundles/page-header/components/translation/TranslationDropdownMenu';
import TranslationDropdownMenuWithGrouping from 'bundles/page-header/components/translation/TranslationDropdownMenuWithGrouping';
import { useCourseLanguageQuery } from 'bundles/page-header/components/translation/useCourseLanguageQuery';

import _t from 'i18n!nls/composable-page-header';

type Props = {
  currentCourseId?: string;
  noBackButton?: boolean;
};

const styles = {
  heading: css`
    padding-top: var(--cds-spacing-200);
    padding-bottom: var(--cds-spacing-200);
  `,
  feedbackButton: css`
    font-weight: normal;
    position: sticky;
    bottom: 0;
    background-color: var(--cds-color-white-0);
    padding: var(--cds-spacing-200) 0;
    border-top: 1px solid var(--cds-color-neutral-stroke-primary);
    border-radius: 0;
  `,
};

const LanguageTranslationSubMenu = ({ currentCourseId, noBackButton }: Props) => {
  const toParentPage = useMobileToParentPage();
  const [languageViewState, setLanguageViewState] = useState<MobileLanguageIdentifierValues>(
    MOBILE_LANGUAGE_IDENTIFIER.LANGUAGE_SUBMENU
  );

  const { loading, languages, courseLanguages } = useCourseLanguageQuery(currentCourseId);
  const locale = getUserLanguageCode();
  if (loading || !languages || !locale) return null;

  const showTranslationDropdownWithGrouping = !!currentCourseId;

  if (languageViewState === MOBILE_LANGUAGE_IDENTIFIER.LANGUAGE_SUBMENU) {
    return (
      <div>
        {!noBackButton && (
          <MobileBackNavHeader
            label={_t('Your Account')}
            ariaLabel={_t('Go to account navigation slide')}
            trackingData={{ name: 'account_header' }}
            onClick={() => toParentPage?.(MOBILE_MAIN_IDENTIFIER.ACCOUNT_MENU)}
            trackingName="mobile_header_account_button"
          />
        )}
        <Typography2 component="h3" variant="subtitleMedium" css={styles.heading}>
          {_t('Change preferred language')}
        </Typography2>
        {showTranslationDropdownWithGrouping ? (
          <TranslationDropdownMenuWithGrouping
            languages={languages}
            currentLanguage={locale}
            courseLanguages={courseLanguages}
          />
        ) : (
          <TranslationDropdownMenu languages={languages} currentLanguage={locale} />
        )}
        <Button
          variant="ghost"
          size="medium"
          fullWidth
          textAlign="start"
          onClick={() => {
            setLanguageViewState(MOBILE_LANGUAGE_IDENTIFIER.LANGUAGE_FEEDBACK);
          }}
          icon={<TranslationIcon size="small" />}
          iconPosition="before"
          css={styles.feedbackButton}
        >
          {_t('Add translation feedback')}
        </Button>
      </div>
    );
  } else {
    return (
      <div>
        <MobileBackNavHeader
          label={_t('Change preferred language')}
          trackingName="mobile_header_back_to_language_submenu_button"
          ariaLabel={_t('Go back to change preferred language')}
          onClick={() => setLanguageViewState(MOBILE_LANGUAGE_IDENTIFIER.LANGUAGE_SUBMENU)}
        />
        <LanguageTranslationFeedbackSubMenu
          onClose={() => setLanguageViewState(MOBILE_LANGUAGE_IDENTIFIER.LANGUAGE_SUBMENU)}
        />
      </div>
    );
  }
};

export default LanguageTranslationSubMenu;
